import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Star,
  Award,
  Users,
  BookOpen,
  Globe,
  Coffee,
  Music,
  Camera,
  Menu,
  X,
} from "lucide-react";
import { useLanguage } from "../../utils/LanguageContext";
import content from "../../content/instructor.json"

const InstructorsPage = () => {

  const { language, setLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const t = content[language];

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Enhanced 3D Sound Wave Logo
  const SoundWaveLogo = () => {
    return (
      <div className="w-10 h-10 bg-gradient-to-r from-[#0B1F3B] to-[#0F172A] rounded-lg flex items-center justify-center relative overflow-hidden shadow-lg">
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="11" width="2" height="2" rx="1" fill="currentColor" />
          <rect x="5.5" y="9" width="2" height="6" rx="1" fill="currentColor" />
          <rect x="9" y="7" width="2" height="10" rx="1" fill="currentColor" />
          <rect
            x="12.5"
            y="4"
            width="2"
            height="16"
            rx="1"
            fill="currentColor"
          />
          <rect x="16" y="8" width="2" height="8" rx="1" fill="currentColor" />
          <rect
            x="19.5"
            y="10"
            width="2"
            height="4"
            rx="1"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  };

  // Language Toggle Component
  const LanguageToggle = () => (
    <div className="flex items-center space-x-2  bg-[#011f4b] rounded-full p-1 w-[100px] sm:w-[100px]">
      <button
        onClick={() => setLanguage("es")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${language === "es"
            ? "bg-lexora-accent text-white shadow-lg"
            : "text-white hover:text-white"
          }`}
      >
        ES
      </button>
      <button
        onClick={() => setLanguage("en")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${language === "en"
            ? "bg-lexora-accent text-white shadow-lg"
            : "text-white hover:text-white"
          }`}
      >
        EN
      </button>
    </div>
  );


  return (
    <div className="min-h-screen bg-lexora-dark">
      {/* <Header /> */}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-[#0D2642] overflow-hidden">
        <img class="absolute inset-0 w-full h-full object-cover" alt="background" src="assests\images\final-inner-bg.png"></img>
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 bg-lexora-accent/20 rounded-full blur-3xl"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="leading-tight xl:leading-tight text-4xl sm:text-5xl md:text-5xl lg:text-5xl font-heading-custom font-bold text-white leading-heading tracking-heading mb-6"
          >
            <span className="block">{t.hero.title}</span>
            <span className="block gradient-text">{t.hero.subtitle}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white mb-8 leading-relaxed max-w-3xl mx-auto"
          >
            {t.hero.description}
          </motion.p>
        </div>
      </section>

      {/* Instructors Section */}
      <section className="py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="space-y-10 sm:space-y-10 md:space-y-10 lg:space-y-16 xl:space-y-16 2xl:space-y-16"
          >
            {t.instructors.map((instructor, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="dark-panel rounded-3xl p-8 lg:p-12 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-accent/10 rounded-full blur-2xl"></div>

                <div className="grid lg:grid-cols-3 gap-8 items-center md:justify-center sm:justify-center justify-center relative z-10">
                  {/* Photo Placeholder */}
                  <div className="lg:col-span-1">
                    <div className="relative">
                      <div className="w-44 h-44 md:w-72 md:h-72 lg:w-80 lg:h-80 xl:w-80 xl:h-80 2xl:w-96 2xl:h-96 mx-auto rounded-full flex items-center justify-center overflow-hidden">
                        <div className="text-8xl text-white">
                          {index === 0 && (<img src="assests/images/Pav-Sekhon.jpg"
                            alt="teacher" className="w-full h-full object-cover"
                            
                          />
                          )}
                          {index === 1 && (<img src="assests/images/stevens.jpg"
                            alt="teacher" className="w-full h-full object-cover"
                            
                          />
                          )}
                          {index === 2 && "👩‍🎓"}
                        </div>
                      </div>
                      {/* <div className="absolute bottom-2 right-2 bg-[rgba(74,124,255,0.6)] text-white rounded p-3 shadow-lg">
                        {instructor.icon}
                      </div> */}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:col-span-2 text-center lg:text-left">
                    <div className="flex items-center justify-center lg:justify-start space-x-2 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${i < Math.floor(instructor.rating)
                              ? "text-[#4A7CFF] fill-current"
                              : "text-gray-400"
                            }`}
                        />
                      ))}
                      <span className="text-white ml-2">
                        {instructor.rating}
                      </span>
                    </div>

                    <h3 className="text-3xl font-bold text-white mb-2">
                      {instructor.name}
                    </h3>

                    <p className="text-lexora-accent text-lg font-semibold mb-4">
                      {instructor.specialization}
                    </p>

                    <p className="text-white mb-6 leading-relaxed">
                      {instructor.background}
                    </p>

                    {/* Expertise Tags */}
                    <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
                      {instructor.expertise.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="px-3 py-1 bg-gradient-accent rounded-full text-sm text-white"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>

                    {/* Fun Fact */}
                    <div className="glass-dark rounded-xl p-4 border border-lexora-accent/20">
                      <div className="flex items-center 2xl:justify-start xl:justify-start lg:justify-start md:justify-center space-x-2 mb-2">
                        <Coffee className="w-5 h-5 text-lexora-accent" />
                        <span className="text-white font-medium">
                          {language === "es" ? "Dato Curioso" : "Fun Fact"}
                        </span>
                      </div>
                      <p className="text-white">{instructor.funFact}</p>
                    </div>

                    {/* Experience Badge */}
                    <div className="mt-6 inline-flex items-center space-x-2 bg-gradient-accent rounded px-4 py-2">
                      <Award className="w-5 h-5 text-white" />
                      <span className="text-white font-semibold">
                        {instructor.experience}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24 bg-[#0D2642]">

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-heading-custom font-bold text-white mb-8">
              {language === "es"
                ? "¿Listo para comenzar tu viaje?"
                : "Ready to start your journey?"}
            </h2>
            <p className="text-xl text-[#CBD5E1] mb-8">
              {language === "es"
                ? "Reserva una sesión con uno de nuestros expertos instructores hoy mismo."
                : "Book a session with one of our expert instructors today."}
            </p>
            <a
              href="/app"
              className="btn-primary bg-gradient-accent text-white px-8 py-2 rounded font-semibold 2xl:text-lg xl:text-lg lg:text-lg sm:text-base md:text-base text-sm hover:shadow-xl hover:scale-105 transition-all inline-block"
            >
              {language === "es" ? "Empieza Hoy" : "Start Today"}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-0 sm:py-5 md:py-5 lg:py-5 xl:py-6 2xl:py-6">
        <div className="py-10 2xl:py-14 xl:py-14 max-w-7xl mx-auto px-4 sm:px-4 md:px-4 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <SoundWaveLogo />
            <span className="text-2xl font-heading font-bold text-gray-800 tracking-heading">
              Lexora
            </span>
          </div>
          <p className="text-gray-800 mb-8">
            {language === "es"
              ? "Transformando carreras a través de habilidades excepcionales de comunicación en inglés."
              : "Transforming careers through exceptional English communication skills."}
          </p>
          <div className="flex justify-center space-x-3 sm:space-x-4 md:space-x-4 lg:space-x-6 xl:space-x-6 2xl:space-x-6">
            <a
              href="/"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              Home
            </a>
            <a
              href="/method"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.method}
            </a>
            <a
              href="/instructors"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.instructors}
            </a>
            <a


              href="/success"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.success}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InstructorsPage;
